import {useEffect, useLayoutEffect, useState} from 'react'

/**
 * Given a React.RefObject<HTMLElement>, check whether the element is overflowing.
 * Uses ResizeObserver to track size changes efficiently.
 *
 * @param elementRef A RefObject to any HTMLElement
 * @returns Whether the element's content is overflowing its bounds
 */
export function useElementIsOverflowing<T extends HTMLElement>(elementRef: React.RefObject<T | null>) {
  const [isOverflowing, setIsOverflowing] = useState(false)

  // Read initial overflow state synchronously before paint
  useLayoutEffect(() => {
    const element = elementRef.current
    if (!element) return

    // contentBoxSize gives the content area (content only, no padding/border)
    // scrollWidth is the total scrollable content width
    // If scrollWidth > clientWidth (content area), content is overflowing
    setIsOverflowing(element.scrollWidth > element.clientWidth)
  }, [elementRef]) // Empty deps - refs are stable, run once on mount

  // ResizeObserver for subsequent updates (async, off render path)
  useEffect(() => {
    const element = elementRef.current
    if (!element) return

    const resizeObserver = new ResizeObserver(entries => {
      const entry = entries[0]
      if (!entry) return

      // Use entry.target instead of captured element for safety
      const target = entry.target as HTMLElement
      // clientWidth gives the visible content area (excluding scrollbar if present)
      // scrollWidth is the total scrollable content width
      const clientWidth = target.clientWidth
      const scrollWidth = target.scrollWidth

      setIsOverflowing(scrollWidth > clientWidth)
    })

    resizeObserver.observe(element)

    return () => resizeObserver.disconnect()
  }, [elementRef]) // Empty deps - refs are stable, run once on mount

  return isOverflowing
}
